// Warranty Service - Contract-First Enterprise Approach (SDK types as source of truth)
import type { 
  WarrantyRequest, 
  WarrantyResponse, 
  WarrantyStatsResponse,
  SimpleWarrantyMetricsResponse
} from '@suvadi/api-client';
import { WarrantyManagementApi, Configuration } from '@suvadi/api-client';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/suvadi';

class WarrantyService {
  // Base configuration for SDK with authentication token
  private getWarrantyApi(): WarrantyManagementApi {
    const config = new Configuration({
      basePath: API_BASE_URL,
      accessToken: () => {
        const token = localStorage.getItem('token');
        return token || '';
      }
    });
    return new WarrantyManagementApi(config);
  }

  private getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const error = await response.text();
      throw new Error(`API Error: ${response.status} - ${error}`);
    }
    return response.json();
  }

  // Get all warranties for a user
  async getUserWarranties(userId: string): Promise<WarrantyResponse[]> {
    try {
      const warrantyApi = this.getWarrantyApi();
      const response = await warrantyApi.getWarrantiesByUserId(userId);
      const backendWarranties: WarrantyResponse[] = response.data;
      
      // Return SDK response directly - no conversion needed
      return backendWarranties;
    } catch (error) {
      throw new Error(`API Error: ${error}`);
    }
  }

  // Get warranty by ID
  async getWarranty(warrantyId: string): Promise<WarrantyResponse> {
    try {
      const warrantyApi = this.getWarrantyApi();
      const response = await warrantyApi.getWarranty(Number(warrantyId));
      const backendWarranty: WarrantyResponse = response.data;
      
      // Return SDK response directly - no conversion needed
      return backendWarranty;
    } catch (error) {
      throw new Error(`API Error: ${error}`);
    }
  }

  // Create new warranty
  async createWarranty(warranty: WarrantyRequest): Promise<WarrantyResponse> {
    try {
      const warrantyApi = this.getWarrantyApi();
      
      // Direct SDK usage - no conversion needed!
      const response = await warrantyApi.createWarranty(warranty);
      const backendWarranty: WarrantyResponse = response.data;
      
      // Return SDK response directly
      return backendWarranty;
    } catch (error) {
      throw new Error(`API Error: ${error}`);
    }
  }

  // Update warranty
  async updateWarranty(warrantyId: string, warranty: WarrantyRequest): Promise<WarrantyResponse> {
    try {
      const warrantyApi = this.getWarrantyApi();
      
      // Direct SDK usage - no conversion needed!
      const response = await warrantyApi.updateWarranty(Number(warrantyId), warranty);
      const backendWarranty: WarrantyResponse = response.data;
      
      // Return SDK response directly
      return backendWarranty;
    } catch (error) {
      throw new Error(`API Error: ${error}`);
    }
  }

  // Delete warranty
  async deleteWarranty(id: string): Promise<void> {
    try {
      const warrantyApi = this.getWarrantyApi();
      await warrantyApi.deleteWarranty(Number(id));
      // SDK handles void response - no processing needed
    } catch (error) {
      throw new Error(`API Error: ${error}`);
    }
  }

  // Get comprehensive warranty statistics
  async getWarrantyStatistics(userId: string): Promise<WarrantyStatsResponse> {
    try {
      const api = this.getWarrantyApi();
      const response = await api.getWarrantyStatsByUserId(userId);
      return response.data as WarrantyStatsResponse;
    } catch (error) {
      throw new Error(`API Error: ${error}`);
    }
  }

  // Get simple warranty metrics
  async getSimpleMetrics(userId: string): Promise<SimpleWarrantyMetricsResponse> {
    try {
      const warrantyApi = this.getWarrantyApi();
      const response = await warrantyApi.getSimpleMetricsByUserId(userId);
      return response.data as SimpleWarrantyMetricsResponse;
    } catch (error) {
      throw new Error(`API Error: ${error}`);
    }
  }
}

export const warrantyService = new WarrantyService();

// Export only SDK types - no more frontend compatibility types needed!
export type { WarrantyRequest, WarrantyResponse, WarrantyStatsResponse, SimpleWarrantyMetricsResponse };
